import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../../components/Input";
import Dropdown from "../../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { ListFilters } from "../../../../../interfaces/vendor.interface";
import useDebounce from "../../../../../hooks/useDebounce";

interface FilterProps {
  serviceTypes: any[];
  allVendorTypes: any[];
  status: any[];
  allStatus: any[];
  cities: any[];
  allCities: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
}

const Filter: React.FC<FilterProps> = ({
  serviceTypes, allVendorTypes, status, allStatus, cities, allCities, dropdownSearchData, onDropdownSearchChange,
  initialFilters, onApply, onReset
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  // Calculate initial filtersApplied state
  const isApplied = filters.searchText !== '' ||
    filters.serviceTypeId !== '' ||
    (filters.cities && filters.cities.length > 0) ||
    filters.status !== '1';

  const [filtersApplied, setFiltersApplied] = useState(false);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Effect to handle Real-time application
  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    const applied = filtersToApply.searchText !== '' ||
      filtersToApply.serviceTypeId !== '' ||
      (filtersToApply.cities && filtersToApply.cities.length > 0) ||
      filtersToApply.status !== '1';

    setFiltersApplied(applied);

    onApply(filtersToApply);
  }, [debouncedSearchText, filters.serviceTypeId, filters.cities, filters.status, onApply]);

  const handleInputChange = useCallback((event: any, name: string) => {
    setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleReset = () => {
    const defaults: ListFilters = {
      searchText: '',
      serviceTypeId: '',
      status: '1',
      cities: [],
    };
    setFilters(defaults);
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t("common.filters")}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t("common.clearFilters")}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-6 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t("vendor.list.pageTexts.filters.searchPlaceholder")}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                multiSelect
                values={filters.cities}
                options={(cities || []).map((city: any) => ({
                  label: city.name,
                  value: city.id
                }))}
                allOptions={(allCities || []).map((city: any) => ({
                  label: city.name,
                  value: city.id
                }))}
                id={VALIDATION_FIELD_MAPPING.cities}
                name={VALIDATION_FIELD_MAPPING.cities}
                searchText={dropdownSearchData.cities}
                placeholder={t("vendor.list.pageTexts.filters.selectCities")}
                label={t("vendor.list.pageTexts.filters.cities")}
                onChange={handleInputChange}
                onSearch={onDropdownSearchChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.serviceTypeId && filters.serviceTypeId.toString().trim() ? [filters.serviceTypeId] : []}
                options={(serviceTypes || []).map((serviceType: any) => ({
                  label: serviceType.name,
                  value: serviceType.id,
                }))}
                allOptions={(allVendorTypes || []).map((serviceType: any) => ({
                  label: serviceType.name,
                  value: serviceType.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.serviceType}
                name={VALIDATION_FIELD_MAPPING.serviceType}
                searchText={dropdownSearchData.serviceTypeId}
                onSearch={onDropdownSearchChange}
                placeholder={t("vendor.list.pageTexts.filters.selectServiceType")}
                label={t("vendor.list.pageTexts.filters.serviceType")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.status && filters.status.toString().trim() ? [filters.status] : []}
                options={(status || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                allOptions={(allStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.status}
                name={VALIDATION_FIELD_MAPPING.status}
                searchText={dropdownSearchData.status}
                onSearch={onDropdownSearchChange}
                placeholder={t("vendor.list.pageTexts.filters.selectStatus")}
                label={t("vendor.list.pageTexts.filters.status")}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Filter;